<script setup>
import { ref, computed, nextTick } from 'vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import {
	PROFANITY_OPTIONS,
	SPEAKERS_MIN,
	SPEAKERS_MAX,
	DELAY_MIN,
	DELAY_MAX,
	DELAY_DEFAULT,
	localeOptions,
	localeMatchesSource,
} from './captionRecognition'
import { FALLBACK_CATALOG, isProfanitySupported, languageName, PROFANITY_UNAVAILABLE } from './captionLanguages'

const removeDisfluencies = defineModel('removeDisfluencies', { type: Boolean, default: false })
const profanity = defineModel('profanity', { type: String, default: 'off' })
const speakers = defineModel('speakers', { type: Boolean, default: false })
const maxSpeakers = defineModel('maxSpeakers', { type: [Number, String], default: 4 })
const outputLocale = defineModel('outputLocale', { type: String, default: '' })
const maxDelaySec = defineModel('maxDelaySec', { type: [Number, String], default: DELAY_DEFAULT })

const props = defineProps({
	// The flow's spoken language: picks the spellings and whether profanity filtering works.
	source: { type: String, default: '' },
	// Language catalog; its `profanitySources` say which spoken languages can filter.
	catalog: { type: Object, default: () => FALLBACK_CATALOG },
	// Shown under Speaker labels when turning them off affects translations.
	speakersNote: { type: String, default: '' },
	// { maxSpeakers?, maxDelaySec?, outputLocale?, profanity?, removeDisfluencies? }
	errors: { type: Object, default: () => ({}) },
	disabled: { type: Boolean, default: false },
})

const advancedOpen = ref(false)
const speakersInput = ref(null)
const delayInput = ref(null)

const locales = computed(() => localeOptions(props.source, outputLocale.value))
const localeMismatch = computed(() => !!outputLocale.value && !localeMatchesSource(outputLocale.value, props.source))

// An unsupported language shows (and sends) Off; the draft keeps the saved
// choice so switching back to a supported language restores it.
const profanitySupported = computed(() => isProfanitySupported(props.source, props.catalog))
const profanityShown = computed({
	get: () => (profanitySupported.value ? profanity.value : 'off'),
	set: (value) => { if (profanitySupported.value) profanity.value = value },
})
const localeSelectOptions = computed(() => [{ value: '', text: 'Default' }, ...locales.value.map((l) => ({ value: l.code, text: `${l.label} · ${l.code}` }))])
const delayChanged = computed(() => Number(maxDelaySec.value) !== DELAY_DEFAULT)

function onDelayRange(e) {
	maxDelaySec.value = Number(e.target.value)
}

async function focusField(field) {
	if (field === 'maxDelaySec') advancedOpen.value = true
	await nextTick()
	const el = field === 'maxSpeakers' ? speakersInput.value : field === 'maxDelaySec' ? delayInput.value : null
	el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
	el?.focus()
}

defineExpose({ focusField })
</script>

<template>
	<div class="flex flex-col gap-4" data-testid="recognition-options">
		<h3 class="m-0 text-sm font-semibold text-surface-text-2">Options</h3>
		<div class="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 xl:grid-cols-4">
			<div class="flex flex-col gap-1.5">
				<FormSwitch
					id="rec-disfluencies"
					v-model="removeDisfluencies"
					size="sm"
					label="Remove filler words"
					label-styles="text-tiny font-medium text-surface-text-1"
					:disabled="disabled"
				/>
				<p class="m-0 text-xs text-surface-7">Drops um, uh and er from captions.</p>
			</div>

			<div class="flex flex-col gap-1.5">
				<FormSwitch
					id="rec-speakers"
					v-model="speakers"
					size="sm"
					label="Speaker labels"
					label-styles="text-tiny font-medium text-surface-text-1"
					:disabled="disabled"
				/>
				<p class="m-0 text-xs text-surface-7">Captions start with <span class="font-mono text-surface-8">&gt;&gt;</span> when the speaker changes.</p>
				<div v-if="speakers" class="mt-1 flex items-center gap-2" data-testid="max-speakers">
					<label for="rec-max-speakers" class="m-0 text-xs text-surface-8" aria-hidden="true">Up to</label>
					<input
						id="rec-max-speakers"
						ref="speakersInput"
						aria-label="Maximum speakers"
						v-model.number="maxSpeakers"
						type="number"
						inputmode="numeric"
						:min="SPEAKERS_MIN"
						:max="SPEAKERS_MAX"
						step="1"
						class="h-8 w-16 rounded border-0 bg-surface-3 px-2 text-center text-base tabular-nums text-surface-text-2 focus:bg-surface-4 focus:outline-none focus:ring-0 md:text-tiny"
						:class="errors.maxSpeakers ? 'ring-1 ring-error-light' : ''"
						:aria-invalid="errors.maxSpeakers ? 'true' : undefined"
						aria-describedby="rec-max-speakers-help"
						:disabled="disabled"
					>
					<span id="rec-max-speakers-help" class="text-xs text-surface-8">speakers ({{ SPEAKERS_MIN }}–{{ SPEAKERS_MAX }})</span>
				</div>
				<p v-if="speakers && errors.maxSpeakers" class="m-0 text-xs text-error-light">{{ errors.maxSpeakers }}</p>
				<p v-if="!speakers && speakersNote" class="m-0 text-xs text-gold" data-testid="speakers-gender-note">{{ speakersNote }}</p>
			</div>

			<div class="flex flex-col gap-1.5">
				<FormSelect id="rec-profanity" v-model="profanityShown" label="Profanity" :options="PROFANITY_OPTIONS.map((o) => ({ value: o.value, text: o.label }))" :disabled="disabled || !profanitySupported" />
				<p v-if="!profanitySupported" class="m-0 text-xs text-surface-7" data-testid="profanity-unavailable">{{ PROFANITY_UNAVAILABLE }}</p>
				<p v-else class="m-0 text-xs text-surface-7">Mask keeps the first letter; Remove leaves the word out.</p>
			</div>

			<div v-if="locales.length" class="flex flex-col gap-1.5" data-testid="spelling">
				<FormSelect id="rec-locale" v-model="outputLocale" label="Spelling" :options="localeSelectOptions" :disabled="disabled" />
				<p v-if="errors.outputLocale" class="m-0 text-xs text-error-light">{{ errors.outputLocale }}</p>
				<p v-else-if="localeMismatch" class="m-0 text-xs text-gold" data-testid="spelling-mismatch">This spelling isn't for {{ languageName(source) }}, the spoken language. Choose Default or a {{ languageName(source) }} spelling.</p>
				<p v-else class="m-0 text-xs text-surface-7">Regional spelling of the spoken language, e.g. color or colour. Default follows the language.</p>
			</div>
		</div>

		<div class="border-t border-surface-3 pt-3">
			<button
				type="button"
				class="inline-flex items-center gap-1.5 rounded text-tiny font-medium text-surface-8 transition-colors hover:text-white"
				:aria-expanded="advancedOpen ? 'true' : 'false'"
				aria-controls="rec-advanced"
				data-testid="advanced-toggle"
				@click="advancedOpen = !advancedOpen"
			>
				<icon-base width="12" height="12" class="transition-transform" :class="advancedOpen ? '' : '-rotate-90'"><icon-chevron-down /></icon-base>
				Advanced
				<span v-if="!advancedOpen && delayChanged" class="text-[11px] font-normal text-surface-7">· Finalisation delay {{ Number(maxDelaySec).toFixed(1) }} s</span>
			</button>
			<div v-show="advancedOpen" id="rec-advanced" class="mt-3 flex max-w-xl flex-col gap-2" data-testid="advanced">
				<div class="flex items-baseline justify-between gap-2">
					<label for="rec-delay" class="m-0 text-tiny font-medium text-surface-text-1">Finalisation delay</label>
					<button v-if="delayChanged" type="button" class="text-[11px] font-medium text-main-light hover:underline" :disabled="disabled" @click="maxDelaySec = DELAY_DEFAULT">Reset to {{ DELAY_DEFAULT }} s</button>
				</div>
				<div class="flex items-center gap-3">
					<span class="text-[11px] text-surface-7">Faster</span>
					<input
						type="range"
						:min="DELAY_MIN"
						:max="DELAY_MAX"
						step="0.1"
						:value="maxDelaySec"
						class="h-1 flex-1 cursor-pointer accent-[#3E64FF]"
						aria-label="Finalisation delay, seconds"
						:disabled="disabled"
						@input="onDelayRange"
					>
					<span class="text-[11px] text-surface-7">More accurate</span>
					<div class="flex items-center gap-1">
						<input
							id="rec-delay"
							ref="delayInput"
							v-model.number="maxDelaySec"
							type="number"
							inputmode="decimal"
							:min="DELAY_MIN"
							:max="DELAY_MAX"
							step="0.1"
							class="h-8 w-16 rounded border-0 bg-surface-3 px-2 text-center text-base tabular-nums text-surface-text-2 focus:bg-surface-4 focus:outline-none focus:ring-0 md:text-tiny"
							:class="errors.maxDelaySec ? 'ring-1 ring-error-light' : ''"
							:aria-invalid="errors.maxDelaySec ? 'true' : undefined"
							aria-describedby="rec-delay-help"
							:disabled="disabled"
						>
						<span class="text-xs text-surface-8">s</span>
					</div>
				</div>
				<p v-if="errors.maxDelaySec" class="m-0 text-xs text-error-light">{{ errors.maxDelaySec }}</p>
				<p id="rec-delay-help" class="m-0 text-xs text-surface-7">
					How long a caption line waits before it's final. Lower is faster but less accurate. Default {{ DELAY_DEFAULT }} s ({{ DELAY_MIN }}–{{ DELAY_MAX }} s).
				</p>
			</div>
		</div>
	</div>
</template>
